/* Fractional autoplay transforms; native touch scrolling, no carousel dependency. */
(() => {
  if (customElements.get('mt-inspiration')) return;
  class MTInspiration extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('.mt-inspiration__viewport');
      this.track = this.querySelector('.mt-inspiration__track');
      this.originals = Array.from(this.track?.children || []);
      if (!this.viewport || this.originals.length < 2) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = 0;
      this.paintFrame = 0;
      this.visible = false;
      this.interacted = false;
      this.hovering = false;
      this.focused = false;
      this.drag = null;
      this.shift = 0;
      this.anchor = 0;
      this.rebuild();
      const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, { ...options, signal: this.controller.signal });
      on(this.viewport, 'scroll', () => { if (!this.frame) this.queuePaint(); }, { passive: true });
      on(this.viewport, 'wheel', () => this.takeControl(), { passive: true });
      on(this.viewport, 'keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' '].includes(event.key)) this.takeControl();
      });
      on(this.viewport, 'pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovering = true; this.sync(); }
      });
      on(this.viewport, 'pointerleave', event => {
        if (event.pointerType === 'mouse') { this.hovering = false; this.sync(); }
      });
      on(this.viewport, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.takeControl();
        this.suppressClick = false;
        if (event.pointerType !== 'mouse') return;
        this.drag = { x: event.clientX, scroll: this.viewport.scrollLeft, id: event.pointerId, moved: false };
      });
      on(this.viewport, 'pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const distance = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(distance) > 6) {
          this.drag.moved = true;
          this.classList.add('is-dragging');
          this.viewport.setPointerCapture(event.pointerId);
        }
        if (this.drag.moved) {
          event.preventDefault();
          this.viewport.scrollLeft = this.drag.scroll - distance;
        }
      });
      const endDrag = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.suppressClick = this.drag.moved;
        if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        this.drag = null;
        this.classList.remove('is-dragging');
        this.queuePaint();
      };
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(this.viewport, 'click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; }
      }, { capture: true });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this, 'focusin', () => { this.focused = true; this.sync(); });
      on(this, 'focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.sync();
      });
      on(document, 'visibilitychange', () => this.sync());
      on(this.motion, 'change', () => { this.rebuild(); this.sync(); });
      on(this, 'shopify:block:select', event => {
        this.takeControl();
        const item = event.target.closest('.mt-inspiration__item');
        if (item) this.viewport.scrollLeft = item.offsetLeft - (this.width - this.cardWidth) / 2;
      });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => { this.measure(); this.sync(); });
        this.resizeObserver.observe(this.viewport);
      } else on(window, 'resize', () => { this.measure(); this.sync(); }, { passive: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        }, { threshold: 0 });
        this.observer.observe(this);
      } else { this.visible = true; this.sync(); }
    }

    rebuild() {
      this.stop();
      this.track.querySelectorAll('[data-mt-clone]').forEach(item => item.remove());
      this.loop = !this.motion.matches && this.originals.length >= 6;
      if (this.loop) {
        const clone = item => {
          const copy = item.cloneNode(true);
          copy.dataset.mtClone = '';
          copy.setAttribute('aria-hidden', 'true');
          [copy, ...copy.querySelectorAll('*')].forEach(node => {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
            if (node.matches('a, button, [tabindex]')) node.setAttribute('tabindex', '-1');
          });
          return copy;
        };
        this.track.prepend(...this.originals.map(clone));
        this.track.append(...this.originals.map(clone));
      }
      this.items = Array.from(this.track.children);
      this.cards = this.items.map(item => item.querySelector('.mt-inspiration__card'));
      this.cards.forEach(card => card.style.removeProperty('transform'));
      this.classList.add('is-enhanced');
      this.measure(true);
    }

    measure(reset = false) {
      this.stop();
      const previousStride = this.stride;
      const relative = previousStride ? (this.viewport.scrollLeft - this.shift + this.width / 2) / previousStride : 0;
      this.width = this.viewport.clientWidth;
      this.cardWidth = this.originals[0].getBoundingClientRect().width;
      this.stride = this.cardWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      this.cycle = this.stride * this.originals.length;
      if (!this.width || !this.stride) return;
      const position = reset ? (this.loop ? this.cycle - (this.width - this.cardWidth) / 2 : 0)
        : previousStride ? relative * this.stride - this.width / 2 : this.viewport.scrollLeft;
      this.move(position, true);
    }

    paint(scroll = this.viewport.scrollLeft - this.shift) {
      if (!this.width || !this.stride) return;
      const radius = Math.max(this.width * 1.7, 900);
      const first = Math.max(0, Math.floor((scroll - this.cardWidth * 1.6) / this.stride));
      const last = Math.min(this.cards.length - 1, Math.ceil((scroll + this.width + this.cardWidth * 1.6) / this.stride));
      for (let index = first; index <= last; index++) {
        const card = this.cards[index];
        const distance = index * this.stride + this.cardWidth / 2 - scroll - this.width / 2;
        if (Math.abs(distance) > this.width / 2 + this.cardWidth * 1.6) continue;
        const y = this.motion.matches ? 0 : Math.min(this.width < 750 ? 30 : 112, distance * distance / (2 * radius));
        const angle = this.motion.matches ? 0 : Math.max(-20, Math.min(20, Math.atan(distance / radius) * 180 / Math.PI));
        card.style.transform = this.motion.matches ? 'none'
          : `translate3d(0,${y.toFixed(3)}px,0) rotate(${angle.toFixed(4)}deg)`;
      }
    }

    move(position, rebase = false) {
      this.position = position;
      // Native scroll positions can be rounded. Keep the remainder in a
      // transform and only rebase the native viewport once per card.
      if (rebase || Math.abs(position - this.anchor) >= this.stride) {
        this.viewport.scrollLeft = position;
        this.anchor = this.viewport.scrollLeft;
      }
      this.shift = this.anchor - position;
      this.track.style.transform = `translate3d(${this.shift.toFixed(4)}px,0,0)`;
      this.paint(position);
    }

    stop() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        // Preserve the exact visual position when the user touches the arc.
        this.move(this.position, true);
      }
      this.lastTime = 0;
    }

    queuePaint() {
      if (this.paintFrame) return;
      this.paintFrame = requestAnimationFrame(() => { this.paintFrame = 0; this.paint(); });
    }

    takeControl() {
      this.interacted = true;
      this.sync();
    }

    sync() {
      const canRun = this.dataset.autoplay === 'true' && this.loop && this.width < this.cycle && this.visible && !document.hidden && !this.motion.matches && !navigator.connection?.saveData && !this.interacted && !this.focused && !this.hovering && !window.Shopify?.designMode;
      if (!canRun) {
        this.stop();
        return;
      }
      if (this.frame) return;
      this.anchor = this.viewport.scrollLeft;
      this.position = this.anchor - this.shift;
      this.lastTime = 0;
      const tick = time => {
        const delta = this.lastTime ? Math.min(time - this.lastTime, 50) : 0;
        this.lastTime = time;
        this.position += delta * (this.width < 750 ? .018 : .026);
        if (this.position >= this.cycle * 1.5) this.position -= this.cycle;
        this.move(this.position);
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.stop();
      cancelAnimationFrame(this.paintFrame);
      this.track?.style.removeProperty('transform');
      this.track?.querySelectorAll('[data-mt-clone]').forEach(item => item.remove());
      this.classList.remove('is-enhanced', 'is-dragging');
    }
  }
  customElements.define('mt-inspiration', MTInspiration);
})();
